import React, { useState, useEffect, useMemo } from 'react';
import Select from '@fhnw/components/Form/Select/Select';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl, defineMessages } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { solrSearchContent } from '@kitconcept/volto-solr/actions';
import { encodeConditionTree } from '@kitconcept/volto-solr/components/theme/SolrSearch/SearchConditions';
import ResultItemPreviewImage from '@kitconcept/volto-solr/components/theme/SolrSearch/resultItems/helpers/ResultItemPreviewImage';
import { Icon } from '@plone/volto/components';
import Teaser from '@fhnw/components/Teaser/Teaser';
import {
  createFacetToken,
  mapToEnabledSelectOptions,
  getEnabledTokens,
  EnabledSelectOptions,
  mapTokenToLabel,
  useSearchInputFromQuery,
} from '@fhnw/components/Blocks/SolarSearch/lib';
import TableTemplateCourseView from './TableTemplate/TableTemplateCourse';
import {
  getVocabLabel,
  mapItemToTeaserProps,
} from '../Listing/variations/mapItemToTeaserProps';
import { encodeExtraConditions } from './ExtraConditions';
import { useVocab } from '@fhnw/components/VocabContext/useVocab';

import SearchInput from './SearchInput';
import Button from '@fhnw/components/Button/Button';
import resetSVG from '@plone/volto/icons/reset.svg';

const messages = defineMessages({
  faculty: {
    id: 'Faculty',
    defaultMessage: 'Faculty',
  },
  type: {
    id: 'Type',
    defaultMessage: 'Type',
  },
  location: {
    id: 'Location Placeholder',
    defaultMessage: 'Location',
  },
  clearFilters: {
    id: 'Clear all filters',
    defaultMessage: 'Clear all filters',
  },
  moreResults: {
    id: 'More results',
    defaultMessage: 'More results',
  },
  coursesFound: {
    id: 'Courses found',
    defaultMessage: 'Courses found',
  },
  of: {
    id: 'of',
    defaultMessage: 'of',
  },
});

const CourseView = (props: any) => {
  const { data } = props;
  const { locale } = useIntl();
  const intl = useIntl();
  const batch_size = parseInt(data?.batch_size);
  const showImage = data?.['show-image'];
  const [isMounted, setIsMounted] = useState(false);
  const history = useHistory();
  const [sortReverse, setSortReverse] = useState(false);
  const [fieldOfStudySelect, setFieldsofstudySelect] = useState<
    EnabledSelectOptions[] | []
  >([]);
  const [continueEducationSelect, setcontinueEducationSelect] = useState<
    EnabledSelectOptions[] | []
  >([]);
  const [locationCategorySelect, setLocationCategorySelect] = useState<
    EnabledSelectOptions[] | []
  >([]);

  const fieldsofstudy = useVocab('collective.taxonomy.fieldsofstudy');
  const continueEducationType = useVocab(
    'collective.taxonomy.continuingeducationtype',
  );
  const location = useVocab('collective.taxonomy.city');
  const dateline_tax = useVocab('collective.taxonomy.dateline');

  // Sync FieldOfStudy
  const initialFieldsofstudySelected = useMemo(() => {
    return data.fieldsofstudycourse?.map((token: string) =>
      mapTokenToLabel(token, fieldsofstudy),
    );
  }, [data.fieldsofstudycourse, fieldsofstudy]);

  useEffect(() => {
    if (data.fieldsofstudycourse?.length > 0) {
      setFieldsofstudySelect(initialFieldsofstudySelected);
    } else {
      setFieldsofstudySelect([]);
    }
  }, [data.fieldsofstudycourse, initialFieldsofstudySelected]);

  // Sync ContinueEducationType
  const initialContinueEducationTypeSelected = useMemo(() => {
    return data.continuingeducationtype?.map((token: string) =>
      mapTokenToLabel(token, continueEducationType),
    );
  }, [data.continuingeducationtype, continueEducationType]);

  useEffect(() => {
    if (data.continuingeducationtype?.length > 0) {
      setcontinueEducationSelect(initialContinueEducationTypeSelected);
    } else {
      setcontinueEducationSelect([]);
    }
  }, [data.continuingeducationtype, initialContinueEducationTypeSelected]);

  // Sync ContinueEducationType
  const initialLocationSelected = useMemo(() => {
    return data.citycourse?.map((token: string) =>
      mapTokenToLabel(token, location),
    );
  }, [data.citycourse, location]);

  useEffect(() => {
    if (data.citycourse?.length > 0) {
      setLocationCategorySelect(initialLocationSelected);
    } else {
      setLocationCategorySelect([]);
    }
  }, [data.citycourse, initialLocationSelected]);

  const [searchInput, setSearchInput] = useSearchInputFromQuery(
    'search_continuing_education',
  );

  const dispatch = useDispatch();
  const blockuuid = props.block;
  const result = useSelector(
    (state: any) => state.solrsearch.subrequests?.[blockuuid],
  );

  const enabledTokens = getEnabledTokens(result);
  const fieldsofstudyWithEnable: EnabledSelectOptions[] =
    mapToEnabledSelectOptions(fieldsofstudy, 0, enabledTokens);
  const continueEducationTypeWithEnable: EnabledSelectOptions[] =
    mapToEnabledSelectOptions(continueEducationType, 1, enabledTokens);
  const locationCategoryWithEnable: EnabledSelectOptions[] =
    mapToEnabledSelectOptions(location, 2, enabledTokens);

  const onChangeFieldOfStudySelect = (value: any) => {
    setFieldsofstudySelect(value);
  };
  const onChangecontinueEducationSelect = (value: any) => {
    setcontinueEducationSelect(value);
  };

  const onChangeLocationCategorySelect = (value: any) => {
    setLocationCategorySelect(value);
  };

  const onChangeSearchInput = (value: string) => {
    setSearchInput(value);
  };

  const onClickRow = (item: any) => {
    history.push(item['@id']);
  };

  const onResetClick = () => {
    setSearchInput('');
    setLocationCategorySelect([]);
    setcontinueEducationSelect([]);
    setFieldsofstudySelect([]);
  };

  const hasSearchTerm = searchInput.length >= 3;

  const onSolrSearch = (b_size: number) => {
    const facetConditions = {
      taxonomy_fieldsofstudy: {
        c: createFacetToken(fieldOfStudySelect),
        m: true,
      },
      continuing_education_types: {
        c: createFacetToken(continueEducationSelect),
        m: true,
      },
      location_category: {
        c: createFacetToken(locationCategorySelect),
        m: true,
      },
    };

    const extra_conditions = [
      ['Subject', 'string', { in: data.keywords || [] }],
    ];

    dispatch(
      solrSearchContent(
        '/',
        {
          SearchableText: hasSearchTerm ? searchInput : '',
          group_select: 2,
          doEmptySearch: true,
          facet_conditions: encodeConditionTree(facetConditions),
          extra_conditions: encodeExtraConditions(extra_conditions as any),
          b_size,
          sort_on: hasSearchTerm ? undefined : 'sortable_title',
          sort_order: hasSearchTerm
            ? undefined
            : sortReverse
              ? 'reverse'
              : 'asc',
          lang: locale,
        },
        blockuuid,
      ),
    );
  };

  useEffect(() => {
    setIsMounted(true);
  }, []);

  /* eslint-disable react-hooks/exhaustive-deps */
  React.useEffect(() => {
    if (!batch_size || batch_size > result?.total) {
      onSolrSearch(result?.total);
    } else onSolrSearch(batch_size);
  }, [
    dispatch,
    searchInput,
    blockuuid,
    fieldOfStudySelect,
    continueEducationSelect,
    locationCategorySelect,
    data.keywords,
    sortReverse,
    hasSearchTerm,
    batch_size,
    result?.total,
  ]);

  const onSortable = () => {
    setSortReverse((b) => !b);
  };

  const onMoreResult = () => {
    onSolrSearch(result?.items?.length + batch_size);
  };

  return (
    <div>
      <SearchInput value={searchInput} onChange={onChangeSearchInput} />
      <div className="select-container">
        {data['faculty-filter'] && (
          <Select
            options={fieldsofstudyWithEnable}
            name="taxonomy_fieldsofstudy"
            onChange={onChangeFieldOfStudySelect}
            isMulti={true}
            value={fieldOfStudySelect}
            placeholder={intl.formatMessage(messages.faculty)}
          />
        )}

        {data['type-filter'] && (
          <Select
            options={continueEducationTypeWithEnable}
            name="degree_program_types"
            onChange={onChangecontinueEducationSelect}
            isMulti={true}
            value={continueEducationSelect}
            placeholder={intl.formatMessage(messages.type)}
          />
        )}

        {data['location-filter'] && (
          <Select
            options={locationCategoryWithEnable}
            name="location_category"
            onChange={onChangeLocationCategorySelect}
            isMulti={true}
            value={locationCategorySelect}
            placeholder={intl.formatMessage(messages.location)}
          />
        )}
      </div>

      <div className="reset-toggle-container">
        <div>
          <Button type="button" styles="clear-all" onPress={onResetClick}>
            <Icon name={resetSVG} size="20px" />
            <span>{intl.formatMessage(messages.clearFilters)}</span>
          </Button>
        </div>
        <div>
          {result?.total}
          {`  ${intl.formatMessage(messages.coursesFound)}`}
        </div>
      </div>

      {data?.template === 'table' && (
        <TableTemplateCourseView
          result={result}
          continueEducationType={continueEducationType}
          fieldsofstudy={fieldsofstudy}
          location={location}
          onClickRow={onClickRow}
          hasSortOrder={!hasSearchTerm}
          sortReverse={sortReverse}
          onSortable={onSortable}
        />
      )}

      <div>
        {(data?.template === 'grid' || data?.template === 'list') &&
          isMounted && (
            <div className="resultview-container">
              <ul
                className={
                  data.template === 'grid' ? 'teaser-col-3' : 'teaser-listing'
                }
              >
                {result?.items.map((item: any, index: number) => {
                  if (!item) return null;
                  return (
                    <Teaser
                      key={index}
                      {...mapItemToTeaserProps(
                        {
                          ...item,
                          category: getVocabLabel(
                            dateline_tax,
                            item?.extras?.taxonomy_dateline,
                          ),
                          effective: null,
                        },
                        {
                          locale,
                          ...data,
                          showTeaserHeading: data?.['show-heading'],
                          showTeaserImage: data?.['show-image'],
                          showTeaserText: data?.['show-text'],
                          showExtendedInfo: data?.['show-information'],
                        },
                        data.template === 'grid' ? 'default' : 'linklist',
                      )}
                      imageComponentProps={
                        showImage
                          ? {
                              item,
                              Wrapper: null,
                              height: undefined,
                              width: undefined,
                            }
                          : undefined
                      }
                      imageComponent={
                        showImage ? ResultItemPreviewImage : undefined
                      }
                    />
                  );
                })}
              </ul>
            </div>
          )}
      </div>
      <div className="more-result-container">
        <div className="more-container">
          <button onClick={onMoreResult}>
            {result?.items?.length >= result?.total
              ? ''
              : ` ${intl.formatMessage(messages.moreResults)}`}
          </button>
          <span>{`${result?.items?.length} ${intl.formatMessage(messages.of)} ${result?.total}`}</span>
        </div>
      </div>
    </div>
  );
};

export default CourseView;
